import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, ChevronLeft, ChevronRight, Building2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { AuthedImg, GpsPhotoDialog } from "../../components/GpsPhotoGallery";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtTimeIST, istDateISO, fmtDate } from "../../lib/api";
import { StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { useTerritoryMasters, TerritoryFilterSelects, namesOf, EMPTY_TERRITORY } from "../../components/TerritoryFilter";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, staffInTerritory, useFetchGate } from "../../components/FetchBar";

const ROLE_LABELS = { supervisor: "Supervisors", tax_collector: "Tax Collectors", municipal_admin: "Admins" };

// Phase 99: capture clock in IST (the report day is an IST calendar day too)
const timeOf = (p) => (p.captured_time_ist ? p.captured_time_ist.slice(0, 5) : fmtTimeIST(p.captured_at));
// Phase 100: From–To range of IST days (default last 7 days, max 31 — mirrors the backend cap)
const MAX_DAYS = 31;
const daysAgoIST = (n) => istDateISO(new Date(Date.now() - n * 86400000));
const dayDiff = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
const dayHeader = (iso) => {
  try { return new Date(`${iso}T00:00:00+05:30`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); }
  catch { return iso; }
};
const PRESETS = [["Today", 0], ["7 days", 6], ["30 days", 29]];

export default function FieldPhotosReport() {
  const { code } = useParams();
  const [dateFrom, setDateFrom] = useState(daysAgoIST(6));
  const [dateTo, setDateTo] = useState(istDateISO());
  // Phase 99: cascading territory — Ward narrows to the Zone, Mohalla to the Ward
  const [zoneId, setZoneId] = useState("");
  const [wardId, setWardId] = useState("");
  const [mohallaId, setMohallaId] = useState("");
  const [uploader, setUploader] = useState("");
  const masters = useTerritoryMasters(code);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  // Phase 133: "Uploaded by" comes from the staff directory (scoped to the chosen Zone / Ward), not from the results
  const [staffByRole, setStaffByRole] = useState({ supervisor: [], tax_collector: [], municipal_admin: [] });
  useEffect(() => {
    api.get(`/t/${code}/staff/options`).then((r) => setStaffByRole({ supervisor: [], tax_collector: [], municipal_admin: [], ...(r.data || {}) })).catch(() => {});
  }, [code]);

  // Phase 176: Zone / Ward / Mohalla are multi-select (comma-joined ids) — one shared cascading control
  const area = useMemo(() => ({ zoneId, wardId, mohallaId }), [zoneId, wardId, mohallaId]);
  const allStaff = useMemo(() => Object.values(staffByRole).flat(), [staffByRole]);
  const staffListed = (id, z, w) => !id || allStaff.some((u) => u.id === id && staffInTerritory(u, z, w));
  const pickArea = (next) => {
    setZoneId(next.zoneId); setWardId(next.wardId); setMohallaId(next.mohallaId);
    if (!staffListed(uploader, next.zoneId, next.wardId)) setUploader("");
  };
  const hasArea = !!(zoneId || wardId || mohallaId);
  const uploadCounts = Object.fromEntries((data?.uploaders || []).map((u) => [u.id, u.count]));
  const staffGroups = useMemo(() => {
    const groups = ["supervisor", "tax_collector", "municipal_admin"].map((r) => ({
      role: r, label: ROLE_LABELS[r], items: (staffByRole[r] || []).filter((u) => staffInTerritory(u, zoneId, wardId)),
    })).filter((g) => g.items.length);
    // anyone who uploaded in the fetched range but is not in the directory (former staff) stays selectable
    const known = new Set(allStaff.map((u) => u.id));
    const extra = (data?.uploaders || []).filter((u) => !known.has(u.id));
    if (extra.length) groups.push({ role: "other", label: "Other uploaders", items: extra });
    return groups;
  }, [staffByRole, zoneId, wardId, allStaff, data]);
  const territoryLabel = wardId ? namesOf(wardId, masters.wards, "ward_name", "this ward")
    : zoneId ? namesOf(zoneId, masters.zones, "zone_name", "this zone") : "";

  // Phase 133: nothing loads until Fetch; page changes reuse the applied filters
  const filters = useMemo(() => ({ dateFrom, dateTo, zoneId, wardId, mohallaId, uploader }), [dateFrom, dateTo, zoneId, wardId, mohallaId, uploader]);
  const gate = useFetchGate(filters);
  const a = gate.applied;
  useEffect(() => { setPage(1); }, [gate.appliedKey]);

  // range helpers — keep From <= To and the span within MAX_DAYS before we ask the server
  const spanDays = dateFrom && dateTo ? dayDiff(dateFrom <= dateTo ? dateFrom : dateTo, dateFrom <= dateTo ? dateTo : dateFrom) : 0;
  const rangeTooLarge = spanDays > MAX_DAYS;
  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };
  const applyPreset = (n) => setRange(daysAgoIST(n), istDateISO());
  const activePreset = PRESETS.find(([, n]) => dateFrom === daysAgoIST(n) && dateTo === istDateISO())?.[0];

  const load = useCallback(() => {
    if (!a || !a.dateFrom || !a.dateTo) return;
    const from = a.dateFrom <= a.dateTo ? a.dateFrom : a.dateTo, to = a.dateFrom <= a.dateTo ? a.dateTo : a.dateFrom;
    if (dayDiff(from, to) > MAX_DAYS) return;
    setLoading(true);
    api.get(`/t/${code}/reports/field-photos`,
      { params: { date_from: from, date_to: to,
                  zone_id: a.zoneId || undefined, ward_id: a.wardId || undefined, mohalla_id: a.mohallaId || undefined,
                  uploaded_by: a.uploader || undefined, page } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData({ photos: [], stats: {}, uploaders: [], days: [] }); })
      .finally(() => setLoading(false));
  }, [code, gate.appliedKey, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);

  const photos = data?.photos || [];
  const stats = data?.stats || {};
  const hasMore = photos.length >= (data?.limit || 48);
  const dayCounts = Object.fromEntries((data?.days || []).map((d) => [d.date, d.count]));
  const multiDay = (data?.date_from || a?.dateFrom) !== (data?.date_to || a?.dateTo);
  const appliedArea = !!(a && (a.zoneId || a.wardId || a.mohallaId));
  // group the current page by IST day, newest day first (rows already arrive newest first)
  const groups = photos.reduce((acc, p) => {
    const k = p.captured_date_ist || (p.captured_at || "").slice(0, 10) || "unknown";
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));

  return (
    <div>
      <PageHeader title="Field Photos" subtitle="Every GPS photo captured on properties — field visits and third-party entries, for any range of up to 31 days" />

      {/* filters */}
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-center gap-3 mb-4" testId="photos-filter-form">
        {/* Phase 100: From – To range of IST days */}
        <div className="flex items-center gap-1.5" data-testid="photos-date-range">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Input type="date" value={dateFrom} className="w-40" aria-label="From date" max={dateTo || undefined}
            onChange={(e) => setRange(e.target.value, dateTo)} data-testid="photos-date-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={dateTo} className="w-40" aria-label="To date" min={dateFrom || undefined}
            onChange={(e) => setRange(dateFrom, e.target.value)} data-testid="photos-date-to" />
        </div>
        <div className="inline-flex rounded-md border bg-muted/40 p-0.5" role="group" aria-label="Quick ranges" data-testid="photos-date-presets">
          {PRESETS.map(([label, n]) => (
            <Button key={label} type="button" size="sm" variant={activePreset === label ? "default" : "ghost"} className="h-7 px-2.5 text-xs"
              onClick={() => applyPreset(n)} data-testid={`photos-preset-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              {label}
            </Button>
          ))}
        </div>
        <div className="w-full lg:w-[560px]"><TerritoryFilterSelects masters={masters} value={area} onChange={pickArea} testIdPrefix="photos" /></div>
        <Select value={uploader || "all"} onValueChange={(v) => setUploader(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="photos-uploader-select" data-staff-count={staffGroups.reduce((n, g) => n + g.items.length, 0)}>
            <SelectValue placeholder="Uploaded by" />
          </SelectTrigger>
          <SelectContent data-testid="photos-uploader-options">
            <SelectItem value="all">All uploaders{territoryLabel ? ` in ${territoryLabel}` : ""}</SelectItem>
            {staffGroups.map((g) => (
              <SelectGroup key={g.role} data-testid={`photos-uploader-group-${g.role}`}>
                <SelectLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</SelectLabel>
                {g.items.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`photos-uploader-option-${u.id}`}>
                    {staffLabelText(u.name, u.employee_id)}{u.is_active === false ? " · inactive" : ""}{uploadCounts[u.id] ? ` · ${uploadCounts[u.id]}` : ""}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
            {staffGroups.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="photos-uploader-empty">
                {territoryLabel ? `No staff mapped to ${territoryLabel}` : "No staff found"}
              </div>
            )}
          </SelectContent>
        </Select>
        {hasArea && (
          <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => pickArea(EMPTY_TERRITORY)} data-testid="photos-area-clear">
            Clear area
          </Button>
        )}
        <FetchButton type="submit" size="sm" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={rangeTooLarge || !dateFrom || !dateTo} testId="photos-fetch-button" />
        <FetchDirtyHint dirty={gate.dirty} testId="photos-fetch-dirty" />
        <span className="text-[11px] text-muted-foreground ml-auto" data-testid="photos-day-note">
          {rangeTooLarge ? <span className="text-destructive font-medium">Pick at most {MAX_DAYS} days</span>
            : <>{spanDays} day{spanDays === 1 ? "" : "s"} · dates &amp; times in IST</>}
        </span>
      </FetchForm>

      {/* summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="photos-stats">
        {[["Total photos", stats.total ?? "-", "photos-stat-total"],
          ["Properties covered", stats.properties ?? "-", "photos-stat-properties"],
          ["With GPS", stats.with_gps ?? "-", "photos-stat-gps"],
          ["Uploaders", stats.uploaders ?? "-", "photos-stat-uploaders"]].map(([l, v, tid]) => (
          <Card key={l} className="shadow-sm">
            <CardContent className="p-3.5">
              <p className="text-[11px] text-muted-foreground">{l}</p>
              <p className="text-xl font-semibold tabular-nums" data-testid={tid}>{v}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="photos-fetch-prompt"
            subtitle="Pick a date range (up to 31 days), an area and an uploader above, then click Fetch to load the photos." />
        </CardContent></Card>
      ) : rangeTooLarge && gate.dirty ? (
        <EmptyState title={`Date range too large`}
          subtitle={`Pick at most ${MAX_DAYS} days at a time — use the presets for a quick week or month view.`} />
      ) : data === null ? (
        <Spinner label="Loading photos..." />
      ) : photos.length === 0 ? (
        <EmptyState title={multiDay ? "No photos in this date range" : "No photos on this day"}
          subtitle={appliedArea ? "No photos in the selected area for these dates — widen the zone / ward / mohalla or the date range, then Fetch again."
            : "Try another date range and Fetch again — photos captured by field staff and third-party entries appear here."} />
      ) : (
        <>
          {/* Phase 100: grouped by IST day, newest first */}
          <div className="space-y-5" data-testid="photos-grid">
            {groupKeys.map((day) => (
              <section key={day} data-testid={`photos-day-group-${day}`}>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold" data-testid={`photos-day-header-${day}`}>
                    {dayHeader(day)}
                    <span className="text-muted-foreground font-normal"> · {dayCounts[day] ?? groups[day].length} photo{(dayCounts[day] ?? groups[day].length) === 1 ? "" : "s"}</span>
                  </h3>
                  {(dayCounts[day] ?? 0) > groups[day].length && (
                    <Badge variant="outline" className="text-[10px] font-normal">{groups[day].length} on this page</Badge>
                  )}
                  <div className="flex-1 border-t border-dashed" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
                  {groups[day].map((p) => (
                    <button key={p.file_id} type="button" onClick={() => setActive(p)}
                      className="group text-left rounded-lg border overflow-hidden bg-card shadow-sm hover:shadow-md transition-shadow focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`photo-card-${p.file_id}`} data-day={day}>
                      <div className="relative">
                        <AuthedImg code={code} fileId={p.file_id} alt={p.filename || "photo"}
                          className="w-full h-32 object-cover transition-transform duration-200 group-hover:scale-105" />
                        <span className={`absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full
                          ${p.lat != null ? "bg-emerald-700/90 text-white" : "bg-amber-600/90 text-white"}`}>
                          <MapPin className="h-2.5 w-2.5" /> {p.lat != null ? "GPS" : "No GPS"}
                        </span>
                      </div>
                      <div className="p-2 space-y-0.5">
                        <p className="text-xs font-semibold font-mono truncate">{p.pin}</p>
                        <p className="text-[11px] truncate">{p.owner_name || "-"}</p>
                        <p className="text-[10px] text-muted-foreground truncate" data-testid={`photo-when-${p.file_id}`}>
                          {p.ward_name || "No ward"} • {multiDay ? `${fmtDate(day)} ` : ""}{timeOf(p)} IST
                        </p>
                        {p.mohalla_name && (
                          <p className="text-[10px] text-muted-foreground truncate inline-flex items-center gap-0.5" data-testid={`photo-mohalla-${p.file_id}`}>
                            <MapPin className="h-2.5 w-2.5" /> {p.mohalla_name}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground truncate">
                          {p.uploaded_by_name ? <>by <StaffLabel code={code} userId={p.uploaded_by} name={p.uploaded_by_name} /></> : "Third-party entry"}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* pagination */}
          <div className="flex items-center justify-center gap-3 mt-5">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="photos-prev-button">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Badge variant="secondary" className="text-xs">Page {page}</Badge>
            <Button variant="outline" size="sm" disabled={!hasMore} onClick={() => setPage(page + 1)} data-testid="photos-next-button">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </>
      )}

      <GpsPhotoDialog code={code} photo={active} onClose={() => setActive(null)}
        extra={active && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="photo-dialog-property">
            <Badge variant="outline" className="font-mono text-[10px]">
              <Building2 className="h-3 w-3 mr-1" /> {active.pin}
            </Badge>
            <span className="font-medium">{active.owner_name || "-"}</span>
            <span className="text-muted-foreground">{[active.zone_name, active.ward_name, active.mohalla_name].filter(Boolean).join(" › ")}{active.address ? ` • ${active.address}` : ""}</span>
          </div>
        )} />
    </div>
  );
}
